import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd';
import { Lightbox } from '../../../../components/lightbox';

@Component({
  selector: 'app-new-hsc-modal-v2',
  templateUrl: './new-hsc-modal-v2.component.html',
  styleUrls: ['./new-hsc-modal-v2.component.scss'],
})
export class NewHscModalV2Component implements OnInit, OnDestroy {
  // --- Inputs / Outputs ---
  @Input() settingVisible: boolean = false;
  @Input() fileTypeList: any[] = [];

  @Output() fileList = new EventEmitter();
  @Output() modalStatus = new EventEmitter();

  // --- View refs ---
  @ViewChild('videoEl') videoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('filmstripScroll') filmstripScroll!: ElementRef<HTMLDivElement>;

  // --- Private ---
  private readonly CAMERA_KEY = 'hsc_last_camera_id';
  private onDeviceChange = () => this.refreshDevices();

  // --- 摄像头设备 ---
  deviceList: MediaDeviceInfo[] = [];
  selectedDeviceId: string = '';
  deviceListLoading: boolean = false;
  stream: MediaStream | null = null;

  // --- 视频变换 ---
  /** 累计旋转角度（不取模，保证 CSS transition 始终顺时针） */
  videoRotation: number = 0;
  flipH: boolean = false;
  flipV: boolean = false;
  videoTransform: string = '';

  // --- 照片列表 ---
  imgPathArray: any[] = [];

  // --- 错误弹窗 ---
  cameraErrorVisible: boolean = false;
  cameraErrorType: 'permission' | 'notfound' = 'permission';
  readonly currentOrigin: string = window.location.origin;

  constructor(
    private message: NzMessageService,
    private lightbox: Lightbox,
  ) {}

  // ----------------------------------------------------------------
  // 生命周期
  // ----------------------------------------------------------------

  async ngOnInit(): Promise<void> {
    await this.loadDevices();
    navigator.mediaDevices.addEventListener('devicechange', this.onDeviceChange);
  }

  ngOnDestroy(): void {
    this.stopStream();
    navigator.mediaDevices.removeEventListener('devicechange', this.onDeviceChange);
  }

  // ----------------------------------------------------------------
  // 摄像头设备管理（私有）
  // ----------------------------------------------------------------

  private selectDevice(list: MediaDeviceInfo[]): string {
    const saved = localStorage.getItem(this.CAMERA_KEY);
    const match = saved && list.find((d) => d.deviceId === saved);
    return match ? saved! : (list[0]?.deviceId ?? '');
  }

  private async requestCameraPermission(): Promise<boolean> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      s.getTracks().forEach((t) => t.stop());
      return true;
    } catch (err) {
      const e = err as any;
      console.error('[camera]', e?.name, e?.message, e);
      const notFound = ['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError'].includes(
        e?.name,
      );
      this.cameraErrorType = notFound ? 'notfound' : 'permission';
      return false;
    }
  }

  private async loadDevices(): Promise<void> {
    // 先请求权限，再枚举设备（未授权时 label 为空）
    if (!(await this.requestCameraPermission())) {
      this.cameraErrorVisible = true;
      return;
    }

    await this.refreshDevices();
  }

  private async refreshDevices(): Promise<void> {
    this.deviceListLoading = true;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const newList = devices.filter((d) => d.kind === 'videoinput');
      this.deviceList = newList;
      if (newList.length === 0) {
        this.message.warning('未检测到摄像头设备');
        return;
      }
      const stillExists = newList.find((d) => d.deviceId === this.selectedDeviceId);
      if (!stillExists) {
        this.stopStream();
        this.selectedDeviceId = this.selectDevice(newList);
        if (this.selectedDeviceId) {
          await this.openCamera();
        }
      }
    } finally {
      this.deviceListLoading = false;
    }
  }

  // ----------------------------------------------------------------
  // 摄像头操作
  // ----------------------------------------------------------------

  async openCamera(): Promise<void> {
    this.stopStream();
    if (this.selectedDeviceId) {
      localStorage.setItem(this.CAMERA_KEY, this.selectedDeviceId);
    }
    try {
      const constraints: MediaStreamConstraints = {
        video: this.selectedDeviceId
          ? {
              deviceId: { exact: this.selectedDeviceId },
              width: { ideal: 4096 },
              height: { ideal: 3072 },
            }
          : true,
      };
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.videoEl.nativeElement.srcObject = this.stream;
    } catch {
      this.message.error('无法打开摄像头，请检查设备是否被占用');
    }
  }

  private stopStream(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  // ----------------------------------------------------------------
  // 视频变换
  // ----------------------------------------------------------------

  private updateVideoTransform(): void {
    const deg = this.videoRotation % 360;
    const needsScale = deg === 90 || deg === 270;
    const scaleX = this.flipH ? -1 : 1;
    const scaleY = this.flipV ? -1 : 1;
    this.videoTransform = needsScale
      ? `rotate(${this.videoRotation}deg) scale(${(9 / 16) * scaleX}, ${(9 / 16) * scaleY})`
      : `rotate(${this.videoRotation}deg) scale(${scaleX}, ${scaleY})`;
  }

  rotateVideo(): void {
    this.videoRotation += 90;
    this.updateVideoTransform();
  }

  flipHorizontal(): void {
    this.flipH = !this.flipH;
    this.updateVideoTransform();
  }

  flipVertical(): void {
    this.flipV = !this.flipV;
    this.updateVideoTransform();
  }

  // ----------------------------------------------------------------
  // 照片 / 胶卷
  // ----------------------------------------------------------------

  cameraScan(): void {
    const video = this.videoEl.nativeElement;
    if (!this.stream || !video.videoWidth) {
      this.message.warning('摄像头未就绪');
      return;
    }
    const uid = String(new Date().getTime());
    const canvas = document.createElement('canvas');
    const normalizedDeg = this.videoRotation % 360;
    const rad = (normalizedDeg * Math.PI) / 180;
    const rotated = normalizedDeg % 180 !== 0;
    canvas.width = rotated ? video.videoHeight : video.videoWidth;
    canvas.height = rotated ? video.videoWidth : video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rad);
    ctx.scale(this.flipH ? -1 : 1, this.flipV ? -1 : 1);
    ctx.drawImage(video, -video.videoWidth / 2, -video.videoHeight / 2);
    const src = canvas.toDataURL('image/jpeg');
    this.imgPathArray.push({ uid, src, name: uid + '.jpg', fileType: null });
  }

  openPreview(index: number): void {
    const albums = this.imgPathArray.map((img) => ({
      src: img.src,
      caption: img.name || '',
      thumb: img.url,
    }));
    this.lightbox.open(albums, index, {
      showZoom: true,
      showRotate: true,
      centerVertically: true,
    });
  }

  deleteFile(index: number): void {
    this.imgPathArray = this.imgPathArray.filter((_, i) => i !== index);
  }

  exportFiles(): void {
    if (this.imgPathArray.some((o) => o.fileType === null)) {
      this.message.warning('请选择文件类型');
      return;
    }
    const result = [...this.imgPathArray];
    this.fileList.emit(result);
    this.handleCloseHSCModal();
  }

  onFilmstripWheel(e: WheelEvent): void {
    if (e.deltaY === 0) return;
    e.preventDefault();
    this.filmstripScroll.nativeElement.scrollLeft += e.deltaY;
  }

  // ----------------------------------------------------------------
  // 其他
  // ----------------------------------------------------------------

  handleCloseHSCModal(): void {
    this.stopStream();
    this.imgPathArray = [];
    this.modalStatus.emit('close');
  }
}
